import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import Loader from '../../components/ui/Loader';
import Badge from '../../components/ui/Badge';
import usuarioProcesoService from '../../services/usuarioProcesoService';
import usuarioService from '../../services/usuarioService';
import procesoService from '../../services/procesoService';

export default function Asignaciones() {
  const [loading, setLoading] = useState(true);
  const [asignaciones, setAsignaciones] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [procesos, setProcesos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Cargar usuarios
      let usuariosData = [];
      try {
        usuariosData = await usuarioService.getAllUsuarios();
        setUsuarios(usuariosData || []);
      } catch (usuarioError) {
        console.error('Error cargando usuarios:', usuarioError);
        if (usuarioError.response?.status !== 400) {
          toast.error('Error al cargar usuarios');
        }
        setUsuarios([]);
      }

      // Cargar procesos
      let procesosData = [];
      try {
        procesosData = await procesoService.getAllProcesos();
        setProcesos(procesosData || []);
      } catch (procesoError) {
        console.error('Error cargando procesos:', procesoError);
        if (procesoError.response?.status !== 400) {
          toast.error('Error al cargar procesos');
        }
        setProcesos([]);
      }

      // Cargar asignaciones
      let asignacionesData = [];
      try {
        asignacionesData = await usuarioProcesoService.getAllUsuarioProcesos();
        setAsignaciones(asignacionesData || []);
      } catch (asignacionError) {
        console.error('Error cargando asignaciones:', asignacionError);
        if (asignacionError.response?.status !== 400) {
          toast.error('Error al cargar asignaciones');
        }
        setAsignaciones([]);
      }
    } catch (error) {
      console.error('Error general:', error);
      setAsignaciones([]);
      setUsuarios([]);
      setProcesos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingItem) {
        await usuarioProcesoService.updateUsuarioProceso(editingItem.id, data);
        toast.success('Asignación actualizada');
      } else {
        await usuarioProcesoService.createUsuarioProceso(data);
        toast.success('Asignación creada');
      }
      loadData();
      closeModal();
    } catch (error) {
      toast.error('Error al guardar');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta asignación?')) return;
    
    try {
      await usuarioProcesoService.deleteUsuarioProceso(id);
      toast.success('Asignación eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
      console.error(error);
    }
  };

  const openModal = (item = null) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const filteredAsignaciones = asignaciones.filter(a =>
    a.usuarioNombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.usuario?.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.procesoNombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.proceso?.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.estado?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getEstadoBadge = (estado) => {
    const estadoColors = {
      ASIGNADO: 'primary',
      EN_PROCESO: 'warning',
      FINALIZADO: 'success',
      CANCELADO: 'danger'
    };
    return <Badge variant={estadoColors[estado] || 'secondary'}>{estado}</Badge>;
  };

  if (loading) return <Loader />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-neutral-800">Asignaciones Usuario-Proceso</h1>
          <p className="text-neutral-600 mt-1">Gestión de asignación de usuarios a procesos</p>
        </div>
        <Button onClick={() => openModal()} className="gap-2">
          <Plus className="w-4 h-4" />
          Nueva Asignación
        </Button>
      </div>

      <div className="flex justify-between items-center gap-4">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Buscar asignaciones..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
          />
        </div>
        <Button variant="outline" onClick={loadData} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Usuario</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Proceso</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Estado</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">F. Inscripción</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">F. Finalización</th>
              <th className="text-right p-4 text-sm font-semibold text-neutral-700">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {filteredAsignaciones.length === 0 ? (
              <tr>
                <td colSpan="6" className="p-8 text-center text-neutral-500">
                  No se encontraron asignaciones
                </td>
              </tr>
            ) : (
              filteredAsignaciones.map((asignacion) => (
                <tr key={asignacion.id} className="hover:bg-neutral-50">
                  <td className="p-4 font-medium text-neutral-800">
                    {asignacion.usuarioNombre || asignacion.usuario?.username || asignacion.usuario?.name || 'N/A'}
                  </td>
                  <td className="p-4 text-neutral-600">{asignacion.procesoNombre || asignacion.proceso?.nombre || 'N/A'}</td>
                  <td className="p-4">{getEstadoBadge(asignacion.estado)}</td>
                  <td className="p-4 text-neutral-600">
                    {asignacion.fechaInscripcion 
                      ? new Date(asignacion.fechaInscripcion).toLocaleDateString() 
                      : 'N/A'}
                  </td>
                  <td className="p-4 text-neutral-600">
                    {asignacion.fechaFinalizacion 
                      ? new Date(asignacion.fechaFinalizacion).toLocaleDateString() 
                      : 'N/A'}
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openModal(asignacion)}
                        className="p-2 text-primary hover:bg-primary-50 rounded-md transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(asignacion.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AsignacionModal
        isOpen={showModal}
        onClose={closeModal}
        onSave={handleSave}
        initialData={editingItem}
        usuarios={usuarios}
        procesos={procesos}
      />
    </div>
  );
}

function AsignacionModal({ isOpen, onClose, onSave, initialData, usuarios, procesos }) {
  const [formData, setFormData] = useState({
    usuario: { id: '' },
    proceso: { id: '' },
    estado: 'ASIGNADO',
    observaciones: ''
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        usuario: { id: initialData.usuarioId || initialData.usuario?.id || '' },
        proceso: { id: initialData.procesoId || initialData.proceso?.id || '' },
        estado: initialData.estado || 'ASIGNADO',
        observaciones: initialData.observaciones || ''
      });
    } else {
      setFormData({
        usuario: { id: '' },
        proceso: { id: '' },
        estado: 'ASIGNADO',
        observaciones: ''
      });
    }
  }, [initialData, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? 'Editar Asignación' : 'Nueva Asignación'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Usuario
          </label>
          <select
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            value={formData.usuario.id}
            onChange={(e) => setFormData({ ...formData, usuario: { id: parseInt(e.target.value) } })}
            required
            disabled={!!initialData}
          >
            <option value="">Seleccionar usuario</option>
            {usuarios.map(u => (
              <option key={u.id} value={u.id}>
                {u.username} - {u.nombre || u.email}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Proceso
          </label>
          <select
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            value={formData.proceso.id}
            onChange={(e) => setFormData({ ...formData, proceso: { id: parseInt(e.target.value) } })}
            required
            disabled={!!initialData}
          >
            <option value="">Seleccionar proceso</option>
            {procesos.map(p => (
              <option key={p.id} value={p.id}>{p.nombre}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Estado
          </label>
          <select
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            value={formData.estado}
            onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
            required
          >
            <option value="ASIGNADO">Asignado</option>
            <option value="EN_PROCESO">En Proceso</option>
            <option value="FINALIZADO">Finalizado</option>
            <option value="CANCELADO">Cancelado</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Observaciones
          </label>
          <textarea
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            rows={3}
            value={formData.observaciones}
            onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
          />
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit">
            Guardar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
